import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkle, X, PaperPlaneTilt, Trash, Lightbulb, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Bugün hangi derslerim var?",
  "Bu ay kim ödeme yaptı?",
  "Borçlu öğrencilerim kimler?",
  "Bu hafta toplam ne kadar ders var?",
  "Paketi biten öğrenci var mı?",
  "Bu ay net kazancım ne?",
];

function renderRich(text) {
  // Convert **bold** to <strong> and basic newlines to <br/>; minimal & safe.
  const parts = (text || "").split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(part);
    if (m) return <strong key={i}>{m[1]}</strong>;
    return part.split("\n").map((ln, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>{ln}{j < arr.length - 1 && <br />}</React.Fragment>
    ));
  });
  return parts;
}

export default function AssistantWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem("edufin-asst-messages");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const hasKey = !!(user?.gemini_api_key && user.gemini_api_key.length > 5);

  useEffect(() => {
    try { sessionStorage.setItem("edufin-asst-messages", JSON.stringify(messages.slice(-20))); } catch {}
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages, busy]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    if (!hasKey) {
      toast.error("Önce Ayarlar'da Gemini API anahtarınızı girin.");
      return;
    }
    setInput("");
    const history = messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: msg, t: Date.now() }]);
    setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { message: msg, history });
      setMessages((prev) => [...prev, { role: "assistant", content: r.data.reply, t: Date.now() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = (detail && typeof detail === "object") ? detail.message : (typeof detail === "string" ? detail : "Bir hata oluştu.");
      setMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${message}`, error: true, t: Date.now() }]);
    } finally {
      setBusy(false);
    }
  }

  function clearChat() {
    setMessages([]);
    try { sessionStorage.removeItem("edufin-asst-messages"); } catch {}
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <>
      {/* Floating launcher button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Asistanı kapat" : "AI Asistanı aç"}
        data-testid="assistant-toggle"
        className={`fixed z-[55] bottom-5 right-5 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all
          ${open ? "bg-[#1A2E1C] hover:bg-[#0F1F11]" : "bg-[#3A5A40] hover:bg-[#2E4833] hover:scale-105"}
          text-white`}
      >
        {open ? <X size={22} weight="bold" /> : <Sparkle size={24} weight="duotone" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed z-[54] bottom-24 right-5 left-5 sm:left-auto sm:w-[420px] max-h-[calc(100vh-7rem)] bg-white border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="assistant-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-br from-[#3A5A40] to-[#2E4833] text-white flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkle size={18} weight="duotone" />
              <div className="min-w-0">
                <div className="font-display font-bold text-sm leading-tight">EduFin Asistanı</div>
                <div className="text-[10px] opacity-80">Gemini · Türkçe · Verilerinizden cevap</div>
              </div>
            </div>
            <button
              onClick={clearChat}
              className="opacity-80 hover:opacity-100 p-1.5 rounded-md hover:bg-white/10 transition-colors"
              title="Sohbeti temizle"
              data-testid="assistant-clear"
              disabled={messages.length === 0}
            >
              <Trash size={14} />
            </button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]" data-testid="assistant-messages">
            {!hasKey ? (
              <NoKeyPrompt onGoSettings={() => { setOpen(false); navigate("/ayarlar"); }} />
            ) : messages.length === 0 ? (
              <Welcome onPick={(s) => send(s)} />
            ) : (
              messages.map((m, i) => (
                <Bubble key={`${m.t}-${i}`} role={m.role} error={m.error}>
                  {renderRich(m.content)}
                </Bubble>
              ))
            )}
            {busy && (
              <Bubble role="assistant">
                <span className="inline-flex gap-1 items-center">
                  <Dot delay="0s" /><Dot delay="0.15s" /><Dot delay="0.3s" />
                </span>
              </Bubble>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border bg-white">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={hasKey ? "Sorunuzu yazın… (Enter = gönder)" : "Önce Ayarlar'dan API anahtarı girin"}
                rows={1}
                disabled={!hasKey || busy}
                data-testid="assistant-input"
                className="flex-1 resize-none rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A5A40]/40 focus:border-[#3A5A40] disabled:opacity-50 max-h-32"
                style={{ minHeight: 38 }}
              />
              <Button
                size="icon"
                onClick={() => send()}
                disabled={!hasKey || busy || !input.trim()}
                className="bg-[#3A5A40] hover:bg-[#2E4833] text-white h-[38px] w-[38px] flex-shrink-0"
                data-testid="assistant-send"
              >
                <PaperPlaneTilt size={16} weight="fill" />
              </Button>
            </div>
            <div className="mt-1.5 text-[10px] text-muted-foreground text-center">
              Yapay zeka hatalı yanıtlar verebilir. Önemli kararlar için verileri kendiniz doğrulayın.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ role, error, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`assistant-bubble-${role}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
          isUser
            ? "bg-[#3A5A40] text-white rounded-br-md"
            : error
              ? "bg-[#FFF3E0] text-[#8B4500] border border-[#FFCC99] rounded-bl-md"
              : "bg-white border border-border text-foreground rounded-bl-md"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-[#3A5A40]/60"
      style={{ animation: "asst-bounce 1.1s infinite", animationDelay: delay }}
    />
  );
}

function Welcome({ onPick }) {
  return (
    <div className="space-y-3" data-testid="assistant-welcome">
      <div className="flex items-start gap-2 text-sm text-foreground bg-white border border-border rounded-2xl rounded-bl-md p-3 shadow-sm">
        <Lightbulb size={18} weight="duotone" className="text-[#D97757] mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-medium mb-1">Merhaba! 👋</div>
          <div className="text-muted-foreground">Aşağıdaki örneklerden birini seçin ya da kendi sorunuzu yazın.</div>
        </div>
      </div>
      <div className="grid gap-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="text-left text-sm bg-white border border-border rounded-lg px-3 py-2 hover:border-[#3A5A40] hover:bg-[#F1F5F2] transition-colors"
            data-testid={`assistant-suggestion-${s.slice(0, 12).replace(/\s+/g, '-')}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function NoKeyPrompt({ onGoSettings }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-6" data-testid="assistant-no-key">
      <div className="w-12 h-12 rounded-full bg-[#FFF3E0] flex items-center justify-center">
        <Warning size={22} weight="duotone" className="text-[#D97757]" />
      </div>
      <div className="font-display font-bold">API anahtarı gerekli</div>
      <p className="text-sm text-muted-foreground max-w-xs">
        AI asistanı kullanmak için tek seferlik <strong>ücretsiz</strong> bir Google Gemini API anahtarı gerekiyor (günde 1.000 ücretsiz istek).
      </p>
      <ol className="text-xs text-left text-muted-foreground space-y-1.5 max-w-xs">
        <li>1. <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="text-[#3A5A40] underline">aistudio.google.com/apikey</a> aç</li>
        <li>2. Google hesabınla giriş yap</li>
        <li>3. <strong>"Create API key"</strong> butonuna bas, kopyala</li>
        <li>4. EduFin → Ayarlar → "AI Asistan" kartına yapıştır → Kaydet</li>
      </ol>
      <Button onClick={onGoSettings} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white mt-2" data-testid="assistant-goto-settings">
        Ayarlar'a Git
      </Button>
    </div>
  );
}
